import { defineStore } from 'pinia'
import { orderApi } from '@/api/order'
import type { OrderItem, Cart } from '@/interfaces'

/** Normalize pending-SOS flags from API (new + legacy response shapes). */
function readPendingSosFromResponse(response: unknown): boolean | null {
  if (response === null || response === undefined || typeof response !== 'object') return null
  const r = response as Record<string, unknown>
  if (r.error) return null
  if (r.has_pending_request === true) return true
  if (r.has_pending_request === false) return false
  const inner = r.data
  if (inner && typeof inner === 'object') {
    const d = inner as Record<string, unknown>
    if (d.has_pending_request === true) return true
    if (d.has_pending_request === false) return false
    if (d.pending_sos_request_exist === true) return true
    if (d.pending_sos_request_exist === false) return false
  }
  return null
}

interface OrderState {
  cart: Cart
  orderItems: OrderItem[]
  sosDisable: boolean
  serviceCharge: number
  orderRatingModal: boolean
  /** Total number of items in the active placed order (from get_my_order_cart) */
  activeOrderItemCount: number
  /** Controls whether the desktop OrderConfirmation modal is open (triggered from header cart dropdown) */
  showOrderPanel: boolean
  /** Incremented on addToCart to trigger cart icon bounce animations */
  cartBumpTick: number
}

export const useOrderStore = defineStore('order', {
  state: (): OrderState => ({
    cart: {
      items: [],
      tenant_id: '',
      order_total: 0,
      order_discount: 0,
    },
    orderItems: [],
    sosDisable: false,
    serviceCharge: 0,
    orderRatingModal: false,
    activeOrderItemCount: 0,
    showOrderPanel: false,
    cartBumpTick: 0,
  }),

  getters: {
    cartItemCount: (state): number => state.cart.items.length,
    cartTotal: (state): number => {
      return state.cart.items.reduce((total, item) => {
        return total + (item.total_amount || 0)
      }, 0)
    },
  },

  actions: {
    async getMyOrderCart(params: { active_reservation_id: string }) {
      // Returns placed-order data for badge/history display only.
      // Must NOT overwrite this.cart — the API returns order_items (placed orders),
      // not the pending cart items. Overwriting would set cart.items = undefined,
      // crashing addToCart's findIndex and breaking the cart UI.
      return await orderApi.getMyOrderCart(params)
    },

    async confirmOrderItem(data: OrderItem) {
      const itemId = Number(data.item_id)
      const resId = Number(data.active_reservation_id || 0)
      const attrId = data.attribute_id ? Number(data.attribute_id) : 0
      const payload = {
        item_id: Number.isNaN(itemId) ? 0 : itemId,
        item_name: data.item_name ?? '',
        count: data.count ?? 1,
        item_amount: data.item_amount ?? data.amount ?? 0,
        total_amount: data.total_amount ?? 0,
        amount: data.amount ?? data.item_amount ?? 0,
        item_discount: data.item_discount ?? 0,
        order_comment: data.order_comment ?? '',
        active_reservation_id: Number.isNaN(resId) ? 0 : resId,
        attribute_id: Number.isNaN(attrId) ? 0 : attrId,
      }
      return await orderApi.confirmCustomerOrderItem(payload)
    },

    async createRestaurantRating(data: { rating: number; tenant_id: string }) {
      return await orderApi.createRestaurantRating(data)
    },

    /** POST customer/create_table_waiter_request — does not set sosDisable; follow with check_pending_sos_status. */
    async createCallWaiterRequest(data: { tenant_id: string }) {
      return await orderApi.createCallWaiterRequest(data)
    },

    async checkSOSStatus(params: { tenant_id: string; customer_id: string }) {
      const response = await orderApi.checkPendingSOSStatus(params)
      if (!response.error) {
        const pending = readPendingSosFromResponse(response)
        if (pending !== null) {
          this.sosDisable = pending
        }
      }
      return response
    },

    async setSOSStatus(params: { tenant_id: string }) {
      return await orderApi.setSOSStatus(params)
    },

    addToCart(item: OrderItem): void {
      const itemAmount = item.item_amount || item.amount || 0
      const itemCount = item.count || 1
      const totalAmount = item.total_amount || itemAmount * itemCount

      const orderItem: OrderItem = {
        ...item,
        item_amount: itemAmount,
        count: itemCount,
        total_amount: totalAmount,
      }

      // Guard: ensure items array exists (can be undefined if cart was corrupted by a stale API response)
      if (!Array.isArray(this.cart.items)) {
        this.cart.items = []
      }

      const existingIndex = this.cart.items.findIndex(
        cartItem => cartItem.item_id === item.item_id && cartItem.attribute_id === item.attribute_id
      )

      if (existingIndex > -1) {
        const existingItem = this.cart.items[existingIndex]
        if (existingItem) {
          existingItem.count += itemCount
          existingItem.total_amount = existingItem.count * (existingItem.item_amount || itemAmount)
        }
      } else {
        this.cart.items.push(orderItem)
      }

      this.updateCartTotal()
      this.cartBumpTick += 1
    },

    removeFromCart(itemId: string, attributeId: string): void {
      this.cart.items = this.cart.items.filter(
        item => !(item.item_id === itemId && item.attribute_id === attributeId)
      )
      this.updateCartTotal()
    },

    /**
     * Update an existing cart line. If variation (attribute_id) changed, removes old line and adds new.
     */
    updateCartItem(previousItemId: string, previousAttributeId: string, newItem: OrderItem): void {
      const sameLine =
        newItem.item_id === previousItemId && newItem.attribute_id === previousAttributeId
      const idx = this.cart.items.findIndex(
        item => item.item_id === previousItemId && item.attribute_id === previousAttributeId
      )
      if (idx < 0) {
        this.addToCart(newItem)
        return
      }
      if (sameLine) {
        const item = this.cart.items[idx]
        if (item) {
          item.count = newItem.count
          item.order_comment = newItem.order_comment ?? ''
          item.item_amount = newItem.item_amount ?? item.item_amount
          item.total_amount = newItem.total_amount ?? item.item_amount * newItem.count
          if (newItem.attribute_name !== undefined) item.attribute_name = newItem.attribute_name
        }
      } else {
        this.cart.items = this.cart.items.filter(
          item => !(item.item_id === previousItemId && item.attribute_id === previousAttributeId)
        )
        this.addToCart(newItem)
      }
      this.updateCartTotal()
    },

    updateCartTotal(): void {
      this.cart.order_total = this.cartTotal
    },

    /** Persist cart to localStorage (smart-dine-in only; call from useOrder when in that flow). */
    saveCartToLocalStorage(): void {
      localStorage.setItem('menuItemCart', JSON.stringify(this.cart))
    },

    /** Restore cart from localStorage (call when entering smart-dine-in flow). */
    loadCartFromLocalStorage(): void {
      const saved = localStorage.getItem('menuItemCart')
      if (saved) {
        try {
          this.cart = JSON.parse(saved) as Cart
        } catch (e) {
          console.error('Error loading cart from localStorage', e)
        }
      }
    },

    /** Clear in-memory cart only (e.g. when leaving smart-dine-in). Does not remove from localStorage. */
    clearCartInMemoryOnly(): void {
      this.cart = {
        items: [],
        tenant_id: '',
        order_total: 0,
        order_discount: 0,
      }
    },

    clearCart(): void {
      this.cart = {
        items: [],
        tenant_id: '',
        order_total: 0,
        order_discount: 0,
      }
      localStorage.removeItem('menuItemCart')
    },

    setOrderRatingModal(value: boolean): void {
      this.orderRatingModal = value
    },

    openCartModal(): void {
      this.showOrderPanel = true
    },

    closeCartModal(): void {
      this.showOrderPanel = false
    },

    setActiveOrderItemCount(count: number): void {
      this.activeOrderItemCount = count
    },
  },
})
